// ── netti's mood — the emotional layer behind every answer ──────────────────
//
// An answer is not only words. The organism that speaks them has a body
// (lib/netti/expression) and a small ambient voice (lib/netti/sound), and this
// module decides which of those fit what just happened: a thing found is a
// glow, a thing let go is a rest, a move that took is a nod, a move that
// failed is a shrink, a turn of navigation is a spin. The choice is made on
// the server from FACTS — the ledger of what the tools did, the kind of ask,
// the words netti settled on — never from the model's own self-description,
// so the body can't claim a feeling the turn didn't earn. The client performs
// it (or doesn't — sounds and expressions are the person's to switch off).

import type { AskKind } from './ground';
import type { NettiSoundName } from './sound';

export type MoodName = 'wake' | 'glow' | 'swell' | 'shrink' | 'radiate' | 'spin' | 'think' | 'nod' | 'delight' | 'rest';

export interface Mood {
  name: MoodName;
  /** 0..1 — how far the body travels. */
  intensity: number;
  sound: NettiSoundName | null;
  /** one short reason, for the bench and the logs; never shown to the person. */
  why: string;
}

export type LedgerEntry = { tool: string; ok: boolean; said: string };

export interface MoodInput {
  question: string;
  answer: string;
  ledger?: LedgerEntry[];
  kind?: AskKind;
  /** the rate ladder spoke instead of netti */
  limited?: 'breath' | 'rest' | null;
  /** the transport failed — netti never got to speak */
  failed?: boolean;
  navigated?: boolean;
  links?: number;
}

const LET_GO = new Set(['delete_item', 'delete_project', 'park_project', 'release_fact', 'forget_conversation', 'clear_chat']);
const MADE = new Set(['create_project', 'create_dimension', 'create_skill', 'create_task', 'publish_skill', 'capture_note']);
const CHANGED = new Set(['move_node', 'move_project', 'rename_item', 'update_dimension', 'update_task', 'complete_task', 'set_preference']);

const NOT_HELD_RE = /\b(don'?t hold|do not hold|nothing (in|here|yet|on)|couldn'?t find|can'?t find|no (record|trace|sign) of|not (in|holding)|haven'?t (kept|got|seen)|isn'?t (here|anywhere|in))\b/i;
const FAILED_RE = /\b(couldn'?t|could not|didn'?t (take|happen|work|land|actually)|did not (take|happen)|nothing (moved|changed)|nothing i reached for took|stopped (me|it)|went wrong)\b/i;
const DELIGHT_RE = /\b(love|beautiful|wonderful|lovely|delight|happy to|good to see|glad|nice one|there it is|found it)\b|!/i;

export function pickMood(input: MoodInput): Mood {
  const a = (input.answer ?? '').trim();
  const ledger = input.ledger ?? [];
  const okTools = ledger.filter((d) => d.ok).map((d) => d.tool);
  // a PREVIEW (delete_project / forget_conversation without confirm) is netti
  // weighing something irreversible — not a failure, not an act.
  const preview = ledger.some((d) => !d.ok && /^confirm first/i.test(d.said));
  const failedMutation = ledger.some((d) => !d.ok && !/^confirm first/i.test(d.said) && (LET_GO.has(d.tool) || MADE.has(d.tool) || CHANGED.has(d.tool)));

  if (input.limited === 'rest') return { name: 'rest', intensity: 0.7, sound: 'absorb', why: 'the day cap — netti rests' };
  if (input.limited === 'breath') return { name: 'rest', intensity: 0.4, sound: null, why: 'a breath between thoughts' };
  if (input.failed) return { name: 'shrink', intensity: 0.5, sound: 'oops', why: 'the voice never reached the surface' };
  if (failedMutation || (input.kind === 'action' && FAILED_RE.test(a) && !okTools.some((t) => MADE.has(t) || CHANGED.has(t) || LET_GO.has(t))))
    return { name: 'shrink', intensity: 0.55, sound: 'oops', why: 'an action that did not take' };
  if (preview && !okTools.some((t) => LET_GO.has(t))) return { name: 'think', intensity: 0.45, sound: 'think', why: 'weighing something irreversible' };
  if (okTools.some((t) => LET_GO.has(t))) return { name: 'rest', intensity: 0.5, sound: 'absorb', why: 'something was let go' };
  if (okTools.some((t) => MADE.has(t))) return { name: 'swell', intensity: 0.7, sound: 'done', why: 'something new exists' };
  if (okTools.some((t) => CHANGED.has(t))) return { name: 'nod', intensity: 0.65, sound: 'done', why: 'a change that took' };
  if (input.navigated) return { name: 'spin', intensity: 0.6, sound: 'touch', why: 'carrying the view somewhere' };
  if (NOT_HELD_RE.test(a)) return { name: 'shrink', intensity: 0.35, sound: null, why: 'the memory does not hold it' };
  if (input.kind === 'stats' || input.kind === 'status') return { name: 'radiate', intensity: 0.55, sound: 'radiate', why: 'reading the whole of the memory back' };
  if ((input.links ?? 0) >= 2 || (input.kind === 'lookup' && okTools.length)) return { name: 'glow', intensity: 0.55, sound: 'notice', why: 'something was found' };
  if (input.kind === 'greeting') return { name: 'wake', intensity: 0.6, sound: 'hello', why: 'a hello' };
  if (input.kind === 'history') return { name: 'nod', intensity: 0.4, sound: 'touch', why: 'reaching back into the conversation' };
  if (DELIGHT_RE.test(a)) return { name: 'delight', intensity: 0.6, sound: 'done', why: 'the words are glad' };
  if (/\?\s*$/.test(a)) return { name: 'think', intensity: 0.4, sound: 'think', why: 'a real fork was offered' };
  return { name: 'nod', intensity: 0.35, sound: null, why: 'a plain answer' };
}

/** What the body does while netti is WORKING (a status frame arrived). */
export const WORKING_MOOD: Mood = { name: 'think', intensity: 0.5, sound: 'think', why: 'reaching into the memory' };
